import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import { useStore } from "@/models";
import { observer } from "mobx-react-lite";
import { get_ticker24h } from "@/api/v4/market";
import {
  computeHighlights,
  formatChangeRate,
  formatPrice,
  parseSymbolParts,
} from "@/components/Pages/Market/MarketsPc/utils";
import ReturnRateChart from "@/components/Pages/aicopy/components/ReturnRateChart";
import strategyIconFallback from "assets/images/aicopy/eb3a5f42-0255-4b85-82c6-bd5462a3928b.png";
import { IconArrowRight, IconBot, IconChevronRight, IconUsers } from "./icons";
import styles from "./index.module.less";

const { useTranslation } = Hooks;
const { getUrl, Big } = Util;

function formatReturnRate(raw) {
  const s = String(raw ?? "")
    .replace(/%/g, "")
    .trim();
  const n = parseFloat(s);
  if (Number.isNaN(n)) return "0%";
  if (n > 0) return `+${s}%`;
  if (n < 0) return `${s}%`;
  return "0%";
}

function formatCompactFollowers(n) {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${Big(v / 1e6).toFixedMaxCy(1)}M`;
  if (v >= 1e3) return `${Big(v / 1e3).toFixedMaxCy(1)}K`;
  return String(v);
}

/** Total Profit / AUM：134376.81 → $134.38K，2427551.95 → $2.43M */
function formatUsdAmountCompact(raw) {
  if (raw == null || raw === "") return "--";
  const n = Number(String(raw).replace(/[$,\s]|USDT/gi, "").trim());
  if (!Number.isFinite(n)) return "--";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e9) return `${sign}$${Big(abs / 1e9).toFixedMaxCy(2)}B`;
  if (abs >= 1e6) return `${sign}$${Big(abs / 1e6).toFixedMaxCy(2)}M`;
  if (abs >= 1e3) return `${sign}$${Big(abs / 1e3).toFixedMaxCy(2)}K`;
  return `${sign}$${Big(abs).toFixedMaxCy(2)}`;
}

function CoinIcon({ symbol, fund }) {
  const { base } = parseSymbolParts(symbol);
  const rawLogo = fund?.currencyMap?.[base]?.logo;
  if (rawLogo) {
    return <img src={rawLogo} alt="" className={styles.coinIcon} />;
  }
  return <span className={styles.coinIconFallback}>{(base || "?").slice(0, 1).toUpperCase()}</span>;
}

function HeroShowcase({ leadStrategy = null, returnTrend = null }) {
  const t = useTranslation();
  const { market, fund } = useStore();
  const [movers, setMovers] = useState([]);
  const lead = leadStrategy;

  useEffect(() => {
    if (!market.marketConfig || !Object.keys(market.marketConfig).length) return;
    let cancelled = false;
    get_ticker24h()
      .then((data) => {
        if (cancelled) return;
        const h = computeHighlights(data, market.marketConfig);
        setMovers((h.gainers || []).slice(0, 5));
      })
      .catch(() => {
        if (!cancelled) setMovers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [market.marketConfig, market.marketConfigMd5]);

  const returnRate = lead?.return90d ?? lead?.return_90d;
  const returnText = formatReturnRate(returnRate);
  const isUp = parseFloat(String(returnRate ?? "").replace(/%/g, "")) >= 0;
  const hasTrend = Array.isArray(returnTrend) && returnTrend.length > 0;

  return (
    <div className={styles.heroShowcase}>
      <div className={styles.heroGlow} aria-hidden />
      <div className={styles.card}>
        <div className={styles.showcaseHead}>
          <span className={styles.badgeBrand}>
            <IconBot size={14} /> {t("home.pcTopAiAgent") || "Top AI Agent"}
          </span>
          <span className={styles.liveBadge}>
            <span className={styles.liveDot} /> {t("home.pcLive") || "Live"}
          </span>
        </div>

        {lead ? (
          <>
            <div className={styles.agentRow}>
              <img
                src={lead.avatarUrl || strategyIconFallback}
                alt=""
                className={styles.agentAvatar}
              />
              <div className={styles.agentMeta}>
                <div className={styles.agentName}>{lead.strategyName}</div>
                <div className={styles.agentSub}>
                  <IconUsers size={12} /> {formatCompactFollowers(lead.followerCount)}{" "}
                  {t("aicopy.followers") || "followers"}
                </div>
              </div>
              {hasTrend ? (
                <div className={styles.agentSpark}>
                  <ReturnRateChart data={returnTrend} />
                </div>
              ) : null}
            </div>

            <div className={styles.statGrid}>
              <div className={styles.statCell}>
                <div className={styles.statLabel}>{t("aicopy.totalProfit") || "Total PNL"}</div>
                <div className={classNames(styles.statValue, styles.statValueProfit)}>
                  {formatUsdAmountCompact(lead.totalProfit)}
                </div>
              </div>
              <div className={styles.statCell}>
                <div className={styles.statLabel}>{t("aicopy.returnRate") || "Return"}</div>
                <div className={classNames(styles.statValue, isUp ? styles.up : styles.down)}>{returnText}</div>
              </div>
              <div className={styles.statCell}>
                <div className={styles.statLabel}>{t("aicopy.assetsUnderManagement") || "AUM"}</div>
                <div className={styles.statValue}>{formatUsdAmountCompact(lead.aum)}</div>
              </div>
              <div className={styles.statCell}>
                <div className={styles.statLabel}>{t("aicopy.userCount") || "Followers"}</div>
                <div className={styles.statValue}>{formatCompactFollowers(lead.followerCount)}</div>
              </div>
            </div>

            <div className={styles.showcaseActions}>
              <a
                href={getUrl(`/aicopy/agents/${encodeURIComponent(lead.strategyId)}?deploy=1`)}
                className={styles.btnPrimarySm}
              >
                {t("home.pcDeployAgent") || "Deploy agent"} <IconArrowRight size={16} />
              </a>
              <a href={getUrl("/ai-lab?tab=leaderboard")} className={styles.btnSecondarySm}>
                {t("home.pcLeaderboard") || "Leaderboard"}
              </a>
            </div>
          </>
        ) : (
          <div className={styles.showcaseEmpty}>
            <a href={getUrl("/aicopy")} className={styles.btnPrimarySm}>
              {t("home.pcExploreAiLab") || "Explore AI Lab"} <IconArrowRight size={16} />
            </a>
          </div>
        )}
      </div>

      <div className={classNames(styles.card, styles.moversCard)}>
        <div className={styles.moversHead}>
          <span className={styles.moversTitle}>{t("home.pcTopMovers") || "Top Movers"}</span>
          <a href={getUrl("/market")} className={styles.moversLink}>
            {t("home.Markets") || "Markets"} <IconChevronRight size={14} />
          </a>
        </div>
        <ul className={styles.moversList}>
          {movers.map((item) => {
            const { base } = parseSymbolParts(item.s);
            const cfg = market.marketConfig?.[item.s];
            const change = Number(item.cr ?? item.r ?? 0);
            const isMoverUp = change >= 0;
            return (
              <li key={item.s}>
                <div className={styles.moverRow}>
                  <CoinIcon symbol={item.s} fund={fund} />
                  <span className={styles.moverSymbol}>{base.toUpperCase()}</span>
                  <span className={styles.moverPrice}>
                    ${formatPrice(item.c, cfg?.buyPoint ?? 2)}
                  </span>
                  <span className={classNames(styles.moverChange, isMoverUp ? styles.up : styles.down)}>
                    {formatChangeRate(item)}
                  </span>
                </div>
              </li>
            );
          })}
          {!movers.length ? (
            <li className={styles.moversEmpty}>{t("home.pcLoadingMarkets") || "Loading…"}</li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}

export default observer(HeroShowcase);
